import { PodiumMedal } from '../components/icons/PodiumMedal';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Calendar, Clock, Star, ArrowLeft, Loader2 } from 'lucide-react';

interface WeeklyEvent {
  id: number;
  title: string;
  description: string;
  theme: string;
  start_date: string;
  end_date: string;
  game_mode: string;
  xp_multiplier: number;
  badge_reward?: string;
}

interface EventScore {
  user_id: string;
  name: string;
  avatar_url: string | null;
  total_score: number;
  games_played: number;
  best_score: number;
}

export default function EventPage() {
  const navigate = useNavigate();
  const [currentEvent, setCurrentEvent] = useState<WeeklyEvent | null>(null);
  const [upcoming, setUpcoming] = useState<WeeklyEvent[]>([]);
  const [leaderboard, setLeaderboard] = useState<EventScore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const res = await fetch('/api/events');
        const data = await res.json();
        setCurrentEvent(data.current);
        setUpcoming(data.upcoming || []);
        setLeaderboard(data.leaderboard || []);
      } catch (err) {
        console.error('Erreur chargement events:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, []);

  const getDaysLeft = (endDate: string) => {
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </button>

      {/* Current Event */}
      {currentEvent ? (
        <motion.div
          className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-6 md:p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl flex-shrink-0">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-300 text-xs font-medium">
                  En cours
                </span>
                <span className="px-2 py-0.5 rounded-full bg-yellow-500/30 text-yellow-300 text-xs font-medium">
                  XP x{currentEvent.xp_multiplier}
                </span>
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                {currentEvent.title}
              </h1>
              <p className="text-dark-300">{currentEvent.description}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-dark-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(currentEvent.start_date)} - {formatDate(currentEvent.end_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {getDaysLeft(currentEvent.end_date)}j restants
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/create')}
            className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold px-5 py-3 rounded-lg transition-colors text-lg"
          >
            Participer
          </button>
        </motion.div>
      ) : (
        <motion.div
          className="card text-center py-12 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Trophy className="w-12 h-12 text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 text-lg">Aucun evenement en cours</p>
          <p className="text-dark-500 text-sm mt-1">Revenez bientot !</p>
        </motion.div>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-yellow-400" />
            Classement de l'evenement
          </h2>

          <div className="card divide-y divide-dark-700">
            {leaderboard.map((entry, index) => (
              <div
                key={entry.user_id}
                className="flex items-center gap-4 py-3 px-2"
              >
                <span className={`w-8 text-center font-bold ${
                  index === 0 ? 'text-yellow-400 text-lg' :
                  index === 1 ? 'text-gray-300' :
                  index === 2 ? 'text-orange-400' :
                  'text-dark-400'
                }`}>
                  <PodiumMedal rank={index + 1}/>
                </span>

                {entry.avatar_url ? (
                  <img
                    src={entry.avatar_url}
                    alt={entry.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-dark-600 flex items-center justify-center text-dark-300">
                    {entry.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{entry.name}</p>
                  <p className="text-xs text-dark-400">
                    {entry.games_played} partie{entry.games_played > 1 ? 's' : ''} | Meilleur: {entry.best_score}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-bold text-purple-400">{entry.total_score.toLocaleString()}</p>
                  <p className="text-xs text-dark-400">points</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Upcoming Events */}
      {upcoming.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary-400" />
            Prochains evenements
          </h2>

          <div className="grid gap-4">
            {upcoming
              .filter((e) => currentEvent ? e.id !== currentEvent.id : true)
              .map((event) => (
                <div
                  key={event.id}
                  className="card flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/30 to-secondary-500/30 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white">{event.title}</h3>
                    <p className="text-sm text-dark-400 truncate">{event.description}</p>
                    <p className="text-xs text-dark-500 mt-1">
                      {formatDate(event.start_date)} - {formatDate(event.end_date)} | XP x{event.xp_multiplier}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
